import { Form, Formik, FormikConfig, FormikState, FormikValues } from "formik";
import React from "react";
import { useNavigate } from "react-router-dom";
import { SocialAuthButtons } from "web/react/components/buttons/social-auth-button";
import Conditional from "web/react/components/conditional/conditional";
import { Divider } from "web/react/components/divider";
import { FormikCheckbox, FormikTextField } from "web/react/components/forms/formik";
import { Button } from "web/react/emo/button";
import { Heading } from "web/react/emo/heading";
import { Text } from "web/react/emo/text/text";
import { VStack } from "web/react/emo/v-stack";
import { View } from "web/react/emo/view";
import { useDomViewport } from "web/react/hooks/use-dom-viewport/use-dom-viewport";
import { CaptureType } from "web/redux/ducks/customer-capture-overlay";
import analytics from "web/script/analytics/analytics";
import { gettext, gettextNoop } from "web/script/modules/django-i18n";
import { getHtmlProps } from "web/script/modules/html-tag-props";
import { getUrl } from "web/script/routing/getUrl";
import { shouldTruncateAnalyticsCategory } from "./soft-signup-utils";
import useSoftSignupForm, { SoftSignupFormValues } from "./use-soft-signup-form";

// ensuring we bundle the T&C i18n keys
gettextNoop("account.register.legal_agreement.sign_up_opt_in_terms");
gettextNoop("account.register.legal_agreement.sign_up_opt_in_terms_us_version");
gettextNoop("account.register.legal_agreement.sign_up_and_subscribe_terms");

interface SoftSignupFormProps {
    email: string;
    setEmail: (email: string) => void;
    showSocialAuth?: boolean;
    legalTextKey?: string;
}

function SoftSignupForm({
    email,
    setEmail,
    showSocialAuth = true,
    legalTextKey,
}: SoftSignupFormProps): JSX.Element {
    const navigate = useNavigate();
    const {
        validate,
        onSubmit,
        signInLink,
        showSignInLink,
        title,
        subtitle,
        submitText,
        initialValues,
        captureType,
        requiresOptIn,
        socialAuthProps,
    } = useSoftSignupForm({ email, onExistingEmail });
    const { isTabletViewport, isDesktopViewport } = useDomViewport();

    function onExistingEmail(newEmail: string): void {
        setEmail(newEmail);
        navigate("/login");
    }

    return (
        <VStack spacing={isTabletViewport || isDesktopViewport ? "md" : "sm"}>
            <VStack spacing={"sm"}>
                <Heading as={"h1"} textStyle={"hero-s"}>
                    {title}
                </Heading>
                {subtitle && (
                    <Text textStyle={"body"} color={"secondary"} as={"p"}>
                        {subtitle}
                    </Text>
                )}
            </VStack>
            <VStack spacing={isTabletViewport || isDesktopViewport ? "md" : "sm"}>
                <SignupForm initialValues={initialValues} onSubmit={onSubmit} validate={validate}>
                    {({ isSubmitting }): JSX.Element => (
                        <>
                            <EmailInput captureType={captureType} />
                            <Conditional check={!!showSignInLink}>
                                <Text textStyle={"body-1"} as={"p"}>
                                    {gettext("account.register.already_member.label")}
                                    <a href={signInLink} target={"_blank"} rel="noreferrer">
                                        {gettext("account.register.login_here.link")}
                                    </a>
                                </Text>
                            </Conditional>
                            <Conditional check={!!requiresOptIn}>
                                <MarketingCheckbox captureType={captureType} />
                            </Conditional>
                            <div>
                                <SubmitButton submitText={submitText} disabled={isSubmitting} />
                            </div>
                        </>
                    )}
                </SignupForm>
                <Conditional check={!!showSocialAuth}>
                    <Divider>
                        <Text textStyle={"caption-3"}>{gettext("general.or")}</Text>
                    </Divider>
                    <SocialAuthButtons {...socialAuthProps} />
                </Conditional>
                <LegalText requiresOptIn={requiresOptIn} legalTextKey={legalTextKey} />
            </VStack>
        </VStack>
    );
}

interface SignupFormProps {
    children: (props: FormikState<FormikValues>) => JSX.Element;
    initialValues: SoftSignupFormValues;
    onSubmit: NonNullable<FormikConfig<SoftSignupFormValues>["onSubmit"]>;
    validate: NonNullable<FormikConfig<SoftSignupFormValues>["validate"]>;
}

function SignupForm({ children, initialValues, onSubmit, validate }: SignupFormProps): JSX.Element {
    return (
        <Formik
            initialValues={initialValues}
            onSubmit={onSubmit}
            validate={validate}
            validateOnChange={false}
            validateOnBlur={false}
        >
            {(props): JSX.Element => {
                return (
                    <Form noValidate>
                        <View>
                            <VStack spacing="sm">{children(props)}</VStack>
                        </View>
                    </Form>
                );
            }}
        </Formik>
    );
}

interface LegalTextProps {
    requiresOptIn: boolean;
    legalTextKey?: string;
}

export function LegalText({ requiresOptIn, legalTextKey }: LegalTextProps): JSX.Element {
    const urlProps = {
        terms_and_conditions_link_props: getHtmlProps({
            href: getUrl("helpTermsAndConditions"),
            target: "_blank",
        }),
        privacy_policy_link_props: getHtmlProps({
            href: getUrl("helpPrivacyPolicy"),
            target: "_blank",
        }),
    };

    let text = "";

    if (legalTextKey) {
        // If you want to pass in a key you need to add it explicitly to the gettextNoop lines in this file
        text = gettext(legalTextKey, urlProps);
    } else if (requiresOptIn) {
        text = gettext("account.register.legal_agreement.sign_up_opt_in_terms", urlProps);
    } else {
        text = gettext(
            "account.register.legal_agreement.sign_up_opt_in_terms_us_version",
            urlProps
        );
    }

    return (
        <Text textStyle="body-s" color="secondary">
            <span
                dangerouslySetInnerHTML={{
                    __html: text,
                }}
            />
        </Text>
    );
}

interface MarketingCheckboxProps {
    captureType: string;
}

function MarketingCheckbox({ captureType }: MarketingCheckboxProps): JSX.Element {
    return (
        <FormikCheckbox
            name="marketing_emails"
            onChange={(e) => {
                analytics.event(
                    captureType,
                    e.target.checked ? "tick_box" : "untick_box",
                    "tickbox_marketing"
                );
            }}
            label={gettext("account.register.legal_agreement.sign_up_checkbox_label")}
        />
    );
}

interface EmailInputProps {
    captureType: CaptureType;
}

function EmailInput({ captureType }: EmailInputProps): JSX.Element {
    // Check if we should not truncate the event. TODO: Remove. See: https://lyst.atlassian.net/browse/BEAM-421
    const truncate = captureType ? shouldTruncateAnalyticsCategory(captureType) : true;

    return (
        <FormikTextField
            name="email"
            type="email"
            label={gettext("settings.account.form.email_label")}
            placeholder={gettext("account.register.form.enter_email.placeholder")}
            onFocus={() => {
                analytics.event(
                    captureType,
                    "fill_field",
                    "field_email",
                    false,
                    {},
                    "",
                    {},
                    [],
                    false,
                    truncate
                );
            }}
            autoCapitalize="off"
            autoFocus={false}
            required={true}
        />
    );
}

interface SubmitButtonProps {
    submitText: string;
    disabled: boolean;
    className?: string;
}

function SubmitButton({ submitText, disabled }: SubmitButtonProps): JSX.Element {
    return <Button width="full" disabled={disabled} type="submit" title={submitText} />;
}

SoftSignupForm.SubmitButton = SubmitButton;
SoftSignupForm.EmailInput = EmailInput;
SoftSignupForm.MarketingCheckbox = MarketingCheckbox;
SoftSignupForm.LegalText = LegalText;
SoftSignupForm.SignupForm = SignupForm;
export default SoftSignupForm;
